import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/*
 * Multi-colour confetti overlay drawn from the celebration accent palette.
 *
 * Two implementations layered:
 *   1. A CSS-utility tile pattern (`.bg-confetti`) for the broad ambient
 *      field — cheap and respects reduced-motion when animated.
 *   2. A small SVG ribbon of larger, hand-positioned squares for foreground
 *      "throw" moments (used near the headline and CTA).
 *
 * Slot props:
 *   - `artwork`  — pass a designer-supplied node (PNG/SVG) to override
 *                  the built-in pattern entirely.
 *   - `density`  — "low" | "medium" | "high" controls opacity of the field.
 *   - `animated` — toggles the gentle drift (8s linear).
 */
export type ConfettiProps = {
  className?: string;
  density?: "low" | "medium" | "high";
  animated?: boolean;
  /** Override with a designer-supplied node. Renders instead of the SVG/pattern. */
  artwork?: ReactNode;
};

const DENSITY_OPACITY: Record<NonNullable<ConfettiProps["density"]>, number> = {
  low: 0.55,
  medium: 0.85,
  high: 1
};

export function Confetti({
  className,
  density = "medium",
  animated = false,
  artwork
}: ConfettiProps) {
  if (artwork) {
    return (
      <div aria-hidden className={cn("pointer-events-none", className)}>
        {artwork}
      </div>
    );
  }

  return (
    <div
      aria-hidden
      className={cn("pointer-events-none", className)}
      style={{ opacity: DENSITY_OPACITY[density] }}
    >
      {/* Ambient tile field — drifts when animated */}
      <div className={cn("absolute inset-0 bg-confetti", animated && "animate-confetti-fall")} />

      {/* Foreground "thrown" pieces — larger, accent-coloured, hand-placed */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 320 180"
        preserveAspectRatio="none"
        fill="none"
      >
        <g>
          <rect x="18" y="12" width="6" height="10" rx="1" fill="hsl(var(--accent-magenta))" transform="rotate(18 21 17)" />
          <rect x="60" y="36" width="5" height="9" rx="1" fill="hsl(var(--accent-teal))" transform="rotate(-22 62 40)" />
          <rect x="120" y="8" width="6" height="9" rx="1" fill="hsl(var(--accent-orange))" transform="rotate(34 123 12)" />
          <rect x="180" y="28" width="5" height="9" rx="1" fill="hsl(var(--accent-violet))" transform="rotate(-12 182 32)" />
          <rect x="232" y="14" width="7" height="10" rx="1" fill="hsl(var(--accent-lime))" transform="rotate(26 235 19)" />
          <rect x="288" y="44" width="6" height="9" rx="1" fill="hsl(var(--primary))" transform="rotate(-30 291 48)" />
          <rect x="30" y="86" width="6" height="9" rx="1" fill="hsl(var(--accent-teal))" transform="rotate(-18 33 90)" />
          <rect x="88" y="118" width="5" height="9" rx="1" fill="hsl(var(--accent-magenta))" transform="rotate(40 90 122)" />
          <rect x="156" y="150" width="6" height="10" rx="1" fill="hsl(var(--accent-orange))" transform="rotate(-26 159 155)" />
          <rect x="210" y="98" width="6" height="9" rx="1" fill="hsl(var(--accent-lime))" transform="rotate(14 213 102)" />
          <rect x="268" y="132" width="6" height="9" rx="1" fill="hsl(var(--accent-violet))" transform="rotate(-34 271 136)" />
          <rect x="46" y="160" width="6" height="9" rx="1" fill="hsl(var(--primary))" transform="rotate(22 49 164)" />
        </g>
      </svg>
    </div>
  );
}
